<template>
  <div class="h-full overflow-hidden">
    <BaseTable
      table-id="modifiedPriceCatalogDetails"
      :headers="priceCatalogDetailsHeaders"
      :list="modifiedTableData"
      :total-records="tableDataCount"
      :is-add-new-hidden="mode === 'view'"
      :hide-multiselect-delete="mode === 'view'"
      :selected-page="selectedPage"
      :items-per-page="10"
      hide-grouping
      isSavingLayoutHidden
      :edit-mode="mode === 'edit'"
      @apply-filters="loadData($event, false)"
      @reset-filters="loadData({}, true)"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData($event, true)"
      @add-new="onAddMaterials">
      <template #controls>
        <button
          v-if="mode === 'edit' && hasDeletableItems"
          type="button"
          @click="onDeleteAll"
          class="px-1 py-1 font-normal border border-gray-300 rounded-md order-2">
          <span class="gap-2 flex items-center justify-center text-sm capitalize text-red-500">
            <IconTrashOutline class="size-4" />
            <p>{{ $t('common.actions.deleteAll') }}</p>
          </span>
        </button>
      </template>
      <template #filters>
        <div class="flex items-center justify-center w-full h-full px-4 mx-auto md:w-2/3">
          <div class="flex flex-col w-full h-full">
            <label class="mb-1 text-sm text-gray-400 uppercase">{{
              $t('pricing-catalogs.categories')
            }}</label>
            <BaseMultiSelectDropdown
              v-model="filterGroupItems"
              class="w-full h-full text-sm"
              tags-outside
              :place-holder-text="$t('pricing-catalogs.selectCategory')"
              :list-items="groupItemsDropdown"></BaseMultiSelectDropdown>
          </div>
        </div>
      </template>
      <template #actions="{ item }: any">
        <div
          class="flex items-center justify-center gap-1"
          v-if="item.isFromManager && mode === 'edit'">
          <ActionButton
            :is-enabled="!item.deleted"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as PriceCatalogDetail)" />
        </div>
      </template>
      <template #custom="{ item, header, index }: any">
        <div
          v-if="header === 'material.name'"
          class="pl-4 text-sm transition-all duration-200 cursor-pointer hover:[text-shadow:1px_1px_2px_rgba(8,_106,_216,_0.3)] text-theme-600 hover:text-theme-700"
          @click="onMaterialNameClick(item.material.id)">
          {{ item.material.name }}
        </div>
        <div v-else class="flex items-center justify-center mx-1">
          <div v-if="header === 'includeVat' || header === 'permitDiscount'">
            <BaseToggle
              class="flex flex-col justify-center text-sm"
              :label="header === 'isActive' ? $t(`baseObject.${header}`) : ''"
              :name="`detail-${item.material.code}-${header}`"
              v-model="item[header]"
              @change="onInputChange(index, header)" />
          </div>

          <div v-else>
            <BaseInput
              v-model="item[header]"
              type="number"
              color="base"
              flat
              show-error-as-tooltip
              :disabled="
                !item['price'] && (header === 'maxDiscountValue' || header === 'maxDiscountPercent')
              "
              class="h-8 mb-4 bg-white"
              @changed="onInputChange(index, header)"
              :error="getInputErrors(item.id, header)">
            </BaseInput>
          </div>
        </div>
      </template>
    </BaseTable>
  </div>
  <PriceCatalogDetailsModalAdd
    v-if="isModalAddActive"
    :list="materialsList"
    :total-records="materialsListCount"
    :header-include-vat="headerIncludeVat"
    @multi-select="onMaterialsSelect"
    @refresh-data="onAddMaterials"
    @add-all-filtered="onAddAllMaterials"
    @close="isModalAddActive = false">
  </PriceCatalogDetailsModalAdd>
  <BaseModalConfirm
    v-if="deleteRowItem && modalDeleteRow"
    @cancel="modalDeleteRow = false"
    @confirm="onDeleteSingle">
    <template #title>{{ $t('common.actions.delete') }}</template>
    <template #text>
      {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem?.material?.name }) }}
    </template>
    <template #buttonText>{{ $t('common.actions.delete') }}</template>
  </BaseModalConfirm>

  <BaseModalConfirm
    v-if="modalDeleteAll"
    @cancel="modalDeleteAll = false"
    @confirm="onConfirmDeleteAll">
    <template #title>{{ $t('common.actions.deleteAll') }}</template>
    <template #text>
      {{ $t('messages.info.deleteAllItems') }}
    </template>
    <template #buttonText>{{ $t('common.actions.deleteAll') }}</template>
  </BaseModalConfirm>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { roundValue } from '@/utils/prices';
import { getGroupItemsMaterialsGroupItems } from '@/services/api/groups';
import { getMaterialsWithConditions } from '@/services/api/materials';
import { v4 as uuidv4 } from 'uuid';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import BaseMultiSelectDropdown from '../common/BaseMultiSelectDropdown.vue';
import BaseTable from '../common/BaseTable.vue';
import PriceCatalogDetailsModalAdd from './PriceCatalogDetailsModalAdd.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseInput from '../common/BaseInput.vue';

import type { GroupItem } from '@/types/Groups';
import { RecordMode, type SelectDropdownItem, type TableFilterData } from '@/types/Base';
import type { PriceCatalogDetail } from '@/types/PricingTypes';
import type { Material } from '@/types/Materials';
import ActionButton from '../common/ActionButton.vue';
import { deepCopy } from '@/utils/data';
import { useToastStore } from '@/stores/toast';

const { t: $t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  mode: 'view' | 'edit';
  tableData: PriceCatalogDetail[];
  allModifiedData: PriceCatalogDetail[];
  tableDataCount: number;
  selectedPage: number;
  priceCatalogId: string;
  headerIncludeVat: boolean;
  addedDetails: PriceCatalogDetail[];
}>();

const emit = defineEmits([
  'addAll',
  'materialInfo',
  'addMaterials',
  'refreshTableData',
  'deleteMaterials',
  'updateMaterials',
  'deleteAll',
]);

const priceCatalogDetailsHeaders = computed(() => [
  {
    value: 'material.code',
    text: $t('baseObject.code'),
    type: 'text',
    active: true,
    hasInlineEdit: false,
  },
  {
    value: 'material.name',
    text: $t('document-type.description'),
    type: 'custom',
    active: true,
  },
  {
    value: 'previousPrice',
    text: $t('pricing-catalogs.previousPrice'),
    type: 'text',
    active: true,
  },
  {
    value: 'price',
    text: $t('pricing-catalogs.price'),
    type: 'text',
    active: true,
    hasInlineEdit: true,
  },
  {
    value: 'permitDiscount',
    text: $t('price-catalog.permitDiscount'),
    type: 'check',
    active: true,
    hasInlineEdit: true,
  },
  {
    value: 'maxDiscountPercent',
    text: $t('pricing-catalogs.maxDiscount'),
    type: 'text',
    active: true,
    hasInlineEdit: true,
  },
  {
    value: 'maxDiscountValue',
    text: $t('pricing-catalogs.maxDiscountValue'),
    type: 'text',
    active: true,
    hasInlineEdit: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  {
    value: 'includeVat',
    text: $t('price-catalog.includeVat'),
    type: 'check',
    active: true,
    hasInlineEdit: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: props.mode == 'edit' },
]);

const modifiedTableData = ref(deepCopy<PriceCatalogDetail[]>(props.tableData));

const hasDeletableItems = computed(() => {
  return modifiedTableData.value.length > 0;
});

const isModalAddActive = ref(false);
const isModalAddAllActive = ref(false);

const materialsList = ref<Material[]>([]);
const materialsListCount = ref<number>();

async function loadData(data: TableFilterData, reset?: boolean) {
  const sortColumn =
    data && data.column !== undefined ? priceCatalogDetailsHeaders.value[data.column]?.value : '';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
  };
  if (reset) filterGroupItems.value = [];

  emit('refreshTableData', {
    ...params,
    priceCatalogId: props.priceCatalogId,
    groupItemIds: filterGroupItems.value.map((x) => x.value) as string[],
  });
}

const groupItems = ref<GroupItem[]>();
const groupItemsDropdown = ref<SelectDropdownItem[]>([]);
onMounted(async () => {
  const res = await getGroupItemsMaterialsGroupItems();
  if (res) {
    groupItems.value = res.data;
    initGroupItemDropdown();
  }
});

function initGroupItemDropdown() {
  if (!groupItems.value) return;

  const filteredGroupItems = groupItems.value.filter((x) => {
    if (!x.materialIds || x.materialIds.length === 0) {
      return false;
    }
    return x.materialIds.some((m) => selectedMaterialIds.value.includes(m));
  });

  groupItemsDropdown.value = filteredGroupItems.map((x) => {
    return {
      value: x.id,
      name: x.ancestryName || x.name,
    };
  });
}

const filterGroupItems = ref<SelectDropdownItem[]>([]);

async function onAddMaterials(data: TableFilterData | undefined) {
  materialsList.value = [];
  const res = await getMaterialsWithConditions({
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || '',
    sortAscending: data?.order === 'asc',
    includeDeleted: true,
    priceCatalogId: props.priceCatalogId,
  });
  if (res) {
    const catalogMaterialIds = new Set(
      props.addedDetails
        .filter((x) => x.recordMode == RecordMode.Added)
        .map((item) => item.materialId)
    );
    materialsList.value = res.data.filter((item) => !catalogMaterialIds.has(item.id));
    materialsListCount.value = res.count;
  }
  isModalAddActive.value = true;
}

const selectedMaterialIds = computed(() => {
  return modifiedTableData.value.map((x) => x.materialId);
});

function onMaterialsSelect(items: Material[]) {
  isModalAddActive.value = false;
  const priceCatalogDetails: PriceCatalogDetail[] = [];
  items.forEach((item) => {
    const newPriceCatalogDetail: PriceCatalogDetail = {
      id: uuidv4(),
      isFromManager: true,
      isActive: true,
      priceCatalogId: props.priceCatalogId,
      materialId: item.id,
      price: 0,
      permitDiscount: false,
      includeVat: props.headerIncludeVat,
      material: {
        id: item.id,
        code: item.code,
        name: item.name,
      },
      modified: true,
      recordMode: RecordMode.Added,
    };
    priceCatalogDetails.push(newPriceCatalogDetail);
  });
  if (priceCatalogDetails.length) emit('addMaterials', priceCatalogDetails);
}

const modalDeleteRow = ref(false);
const modalDeleteAll = ref(false);

const deleteRowItem = ref<PriceCatalogDetail>();
function onDeleteRow(item: PriceCatalogDetail) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onDeleteAll() {
  modalDeleteAll.value = true;
}

function onConfirmDeleteAll() {
  modalDeleteAll.value = false;
  emit('deleteAll', props.priceCatalogId);
}

const toastStore = useToastStore();

function onDeleteMultiselect(items: PriceCatalogDetail[]) {
  const itemsToDelete = items.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.info({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }

  emit('deleteMaterials', itemsToDelete);
}

function onDeleteSingle() {
  if (!deleteRowItem.value) return;
  modalDeleteRow.value = false;
  emit('deleteMaterials', [deleteRowItem.value]);
}

// table validation

watch(
  () => props.tableData,
  (newData) => {
    modifiedTableData.value = deepCopy<PriceCatalogDetail[]>(newData);
  },
  { deep: true }
);
watch(
  () => props.allModifiedData,
  (newData) => {
    validationModel.details = newData.map((item) => ({
      price: item.price ?? '',
      previousPrice: item.previousPrice || '',
      maxDiscountPercent: item.maxDiscountPercent || '',
      maxDiscountValue: item.maxDiscountValue || '',
    }));
  },
  { deep: true }
);

const validationModel = reactive({
  details: props.allModifiedData.map((item) => ({
    price: item.price ?? '',
    previousPrice: item.previousPrice || '',
    maxDiscountPercent: item.maxDiscountPercent || '',
    maxDiscountValue: item.maxDiscountValue || '',
  })),
});

const rules = computed(() => ({
  details: {
    $each: helpers.forEach({
      price: {
        required: helpers.withMessage($t('messages.errors.required'), required),
        priceValue: helpers.withMessage($t('messages.errors.invalidValue'), (value: any) => {
          return !isNaN(value) && value > 0;
        }),
      },
      previousPrice: {
        priceValue: helpers.withMessage($t('messages.errors.invalidValue'), (value: any) => {
          if (value === '') return true;
          return !isNaN(value) && value > 0;
        }),
      },
      maxDiscountPercent: {
        priceValue: helpers.withMessage(`${$t('messages.errors.maxValue')} 100`, (value: any) => {
          if (value === '') return true;
          return !isNaN(value) && value >= 0 && value <= 100;
        }),
      },
      maxDiscountValue: {
        priceValue: helpers.withMessage(
          $t('messages.errors.invalidValue'),
          (value: any, detail) => {
            if (value === '') return true;
            return !isNaN(value) && value >= 0 && value <= detail.price;
          }
        ),
      },
    }),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true, $autoDirty: true });

function getInputErrors(
  id: string,
  type: 'price' | 'previousPrice' | 'maxDiscountPercent' | 'maxDiscountValue'
) {
  if (!$v.value.details.$each.$response) return;
  const index = props.allModifiedData.findIndex((x) => x.id === id);
  if (index <= -1) return;
  const validationInput = $v.value.details.$each.$response.$data[index]?.[type];
  if (!validationInput) return '';
  if (validationInput.$invalid && $v.value.details.$each.$response.$errors[index])
    return $v.value.details.$each.$response.$errors[index]?.[type][0].$message;
  else return '';
}

function onInputChange(index: number, header: string) {
  if (header === 'maxDiscountValue') {
    modifiedTableData.value[index].maxDiscountPercent =
      roundValue(
        (modifiedTableData.value[index].maxDiscountValue || 0) /
          modifiedTableData.value[index].price
      ) * 100;
  } else if (header === 'maxDiscountPercent') {
    modifiedTableData.value[index].maxDiscountValue =
      modifiedTableData.value[index].price *
      ((modifiedTableData.value[index].maxDiscountPercent || 0) / 100);
  }
  if (modifiedTableData.value[index].recordMode !== RecordMode.Added)
    modifiedTableData.value[index].recordMode = RecordMode.Modified;
  emit('updateMaterials', modifiedTableData.value[index]);
}

function onAddAllMaterials(
  addAllPrice: number,
  addAllPermitDiscount: boolean,
  tableQuery: string | undefined
) {
  isModalAddAllActive.value = false;
  isModalAddActive.value = false;
  emit('addAll', addAllPrice, addAllPermitDiscount, tableQuery);
}
function onMaterialNameClick(id: string) {
  if (!id) return;
  emit('materialInfo', id);
}
</script>
